import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  pointerWithin,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragStartEvent,
  type DragOverEvent,
  type DragEndEvent,
} from '@dnd-kit/core';

/**
 * Collision strategy for the rows orientation (horizontal card scrollers in
 * stacked bands). `closestCorners` alone misses two drop targets the suite
 * builder needs: an *empty* band and the trailing space *after* the last card —
 * because the nearest card corner in another band wins. Preferring pointer hits
 * fixes both: when the pointer is over a card we still resolve to that card (so
 * card-level placement works); when it's only over the band's droppable
 * (empty band or trailing space) we resolve to the column, which appends.
 */
const rowsCollisionDetection: CollisionDetection = (args) => {
  const pointer = pointerWithin(args);
  if (pointer.length > 0) {
    const cardHit = pointer.find((c) => !String(c.id).startsWith('column-'));
    return cardHit ? [cardHit] : pointer;
  }
  return closestCorners(args);
};
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { BoardColumn } from './BoardColumn';
import { BoardCard } from './BoardCard';
import { useBoardAutoScroll } from './useBoardAutoScroll';
import { deriveDropPosition, sameIdOrder } from './dropPosition';
import type {
  BoardColumnConfig,
  BoardCardRenderState,
  BoardDropEvent,
  BoardDropResult,
  BoardOrientation,
  BoardColumnLayout,
  BoardTelemetryEvent,
} from './types';

/**
 * How long the post-drop snapshot may shadow `itemsByColumn` while waiting
 * for the consumer's data to converge. Comfortably past the 180ms overlay
 * drop animation; short enough that a consumer that never converges (failed
 * mutation rolled back, unpersisted drop) visibly reverts quickly and
 * external/realtime updates are never masked for long.
 */
const SETTLE_TIMEOUT_MS = 500;

interface BoardProps<TColumnId extends string, TItem> {
  columns: readonly BoardColumnConfig<TColumnId>[];
  itemsByColumn: Record<TColumnId, TItem[]>;
  getItemId: (item: TItem) => string;
  getItemColumnId: (item: TItem) => TColumnId;
  renderCard: (item: TItem, state: BoardCardRenderState) => React.ReactNode;
  /** If false, card is not draggable (stays clickable). */
  isItemDraggable?: (item: TItem) => boolean;
  /** Called when a drop is attempted; return false to reject. Also drives drop-target tinting during drag. */
  validateDrop?: (fromColumnId: TColumnId, toColumnId: TColumnId, item: TItem) => boolean;
  /** Return `false` to reject the drop (board reverts immediately); see {@link BoardDropResult}. */
  onDrop: (event: BoardDropEvent<TColumnId, TItem>) => BoardDropResult;
  /** Optional lifecycle telemetry for feature-specific breadcrumbs. */
  onTelemetry?: (event: BoardTelemetryEvent<TColumnId, TItem>) => void;
  onCardClick?: (item: TItem) => void;
  /**
   * Explicit height for the scroll container. When omitted (default), the
   * board fills its flex parent (`flex-1 min-h-0`) so the columns reach the
   * bottom of the available space and scroll internally — no page scroll. The
   * parent must be a flex column with a bounded height for fill mode to work.
   */
  height?: string;
  /** Layout orientation. Defaults to 'columns' (horizontal strip of columns). */
  orientation?: BoardOrientation;
  /**
   * Id of a column to render as a left **staging rail** instead of a band.
   * Only honored when `orientation === 'rows'`. The rail column renders as a
   * borderless vertical card list on the left; the remaining columns stack as
   * bands on the right. Drags between the rail and bands share this Board's one
   * DndContext, so cross-container moves "just work". Unset (default) → no rail,
   * tasks board and plain rows behavior unchanged.
   */
  railColumnId?: TColumnId;
  /** Rendered at the start of the bands scroll area (rail layout only) — e.g. a read-only Seed band. */
  header?: React.ReactNode;
  /** Rendered at the end of the bands scroll area (rail layout only) — e.g. an "Add layer" button. */
  footer?: React.ReactNode;
  className?: string;
}

export function Board<TColumnId extends string, TItem>({
  columns,
  itemsByColumn,
  getItemId,
  getItemColumnId,
  renderCard,
  isItemDraggable,
  validateDrop,
  onDrop,
  onTelemetry,
  onCardClick,
  height,
  orientation = 'columns',
  railColumnId,
  header,
  footer,
  className,
}: BoardProps<TColumnId, TItem>) {
  const [activeItem, setActiveItem] = useState<TItem | null>(null);
  const [sourceColumnId, setSourceColumnId] = useState<TColumnId | null>(null);
  const [overColumnId, setOverColumnId] = useState<TColumnId | null>(null);
  // Transient snapshot of the column→items map with three states:
  //  - idle: null — render straight from itemsByColumn.
  //  - dragging: live mid-drag snapshot, spliced by onDragOver so the target
  //    column's SortableContext shifts neighbors (canonical dnd-kit
  //    multi-container pattern).
  //  - settling: post-drop snapshot holding the dropped order until the
  //    consumer's itemsByColumn converges (optimistic update/refetch) or the
  //    SETTLE_TIMEOUT_MS fallback fires.
  const [dragItems, setDragItems] = useState<Record<TColumnId, TItem[]> | null>(null);
  // Columns the last drop touched — the settle effect compares only these,
  // so unrelated external updates can't block convergence.
  const lastDropRef = useRef<{
    from: TColumnId;
    to: TColumnId;
    item: TItem;
    itemId: string;
    beforeItemId: string | null;
    afterItemId: string | null;
  } | null>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSettleTimer = useCallback(() => {
    if (settleTimerRef.current !== null) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }, []);
  useEffect(() => clearSettleTimer, [clearSettleTimer]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const {
    scrollRef,
    onDragMove,
    onDragEnd: onAutoScrollEnd,
  } = useBoardAutoScroll<HTMLDivElement>();

  const countItemsByColumn = useCallback(
    (source: Record<TColumnId, TItem[]>): Record<TColumnId, number> =>
      Object.fromEntries(columns.map((col) => [col.id, (source[col.id] ?? []).length])) as Record<
        TColumnId,
        number
      >,
    [columns]
  );

  const emitTelemetry = useCallback(
    (
      event: Omit<BoardTelemetryEvent<TColumnId, TItem>, 'columnItemCounts'> & {
        sourceItems?: Record<TColumnId, TItem[]>;
        snapshotItems?: Record<TColumnId, TItem[]>;
      }
    ) => {
      if (!onTelemetry) return;
      const { sourceItems, snapshotItems, ...payload } = event;
      onTelemetry({
        ...payload,
        columnItemCounts: countItemsByColumn(sourceItems ?? itemsByColumn),
        snapshotColumnItemCounts: snapshotItems ? countItemsByColumn(snapshotItems) : undefined,
      });
    },
    [countItemsByColumn, itemsByColumn, onTelemetry]
  );

  const resolveColumnId = (
    overId: string | number,
    overData: Record<string, unknown> | undefined
  ): TColumnId | null => {
    const id = String(overId);
    if (id.startsWith('column-')) return id.slice('column-'.length) as TColumnId;
    if (overData?.columnId) return overData.columnId as TColumnId;
    const item = overData?.item as TItem | undefined;
    if (item) return getItemColumnId(item);
    return null;
  };

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const item = event.active.data.current?.item as TItem | undefined;
      if (!item) return;
      const from = getItemColumnId(item);
      clearSettleTimer();
      lastDropRef.current = null;
      setActiveItem(item);
      setSourceColumnId(from);
      setOverColumnId(from);
      setDragItems(itemsByColumn);
      emitTelemetry({
        phase: 'drag_start',
        item,
        itemId: getItemId(item),
        sourceColumnId: from,
        overColumnId: from,
        toColumnId: from,
        beforeItemId: null,
        afterItemId: null,
        validationResult: null,
        dropAccepted: null,
        sourceItems: itemsByColumn,
      });
    },
    [getItemColumnId, itemsByColumn, clearSettleTimer, emitTelemetry, getItemId]
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { over } = event;
      if (!over || !activeItem) return;
      const to = resolveColumnId(over.id, over.data.current);
      if (!to) return;
      const from = sourceColumnId ?? getItemColumnId(activeItem);
      const activeId = getItemId(activeItem);
      if (to !== overColumnId) {
        emitTelemetry({
          phase: 'drag_over',
          item: activeItem,
          itemId: activeId,
          sourceColumnId: from,
          overColumnId: to,
          toColumnId: to,
          beforeItemId: null,
          afterItemId: null,
          validationResult: validateDrop ? validateDrop(from, to, activeItem) : null,
          dropAccepted: null,
          sourceItems: dragItems ?? itemsByColumn,
        });
      }
      setOverColumnId(to);

      // Splice only for CROSS-column moves. For same-column reorder, let
      // useSortable + verticalListSortingStrategy shift neighbors natively;
      // mutating the items list there causes feedback loops with the
      // collision detection (the very issue that made drops land off-by-one).
      setDragItems((current) => {
        const base = current ?? itemsByColumn;
        const fromCol = (Object.keys(base) as TColumnId[]).find((cid) =>
          base[cid].some((it) => getItemId(it) === activeId)
        );
        if (!fromCol || fromCol === to) return base;

        // Different column: move active out of its old column and into the
        // target so the target's SortableContext renders a slot for it.
        const overId = String(over.id);
        const isColumnDrop = overId.startsWith('column-');
        const targetWithoutActive = (base[to] ?? []).filter((it) => getItemId(it) !== activeId);

        let insertAt: number;
        if (isColumnDrop) {
          insertAt = targetWithoutActive.length;
        } else {
          const idx = targetWithoutActive.findIndex((it) => getItemId(it) === overId);
          insertAt = idx < 0 ? targetWithoutActive.length : idx;
        }

        const next = { ...base };
        next[fromCol] = base[fromCol].filter((it) => getItemId(it) !== activeId);
        next[to] = [
          ...targetWithoutActive.slice(0, insertAt),
          activeItem,
          ...targetWithoutActive.slice(insertAt),
        ];
        return next;
      });
    },
    [
      activeItem,
      sourceColumnId,
      overColumnId,
      itemsByColumn,
      dragItems,
      getItemId,
      getItemColumnId,
      validateDrop,
      emitTelemetry,
    ]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { over } = event;
      const item = activeItem;
      const from = sourceColumnId;
      const snapshot = dragItems;
      // Reset transient drag state up front, but DON'T clear dragItems yet —
      // see the eager-write below. Clearing it here would let the items list
      // snap back to pre-drag order for the render between this handler and
      // the optimistic cache update, which is what produced the "hop"
      // animation on neighbors.
      setActiveItem(null);
      setSourceColumnId(null);
      setOverColumnId(null);
      onAutoScrollEnd();

      const emitCancelDrag = () => {
        emitTelemetry({
          phase: 'drag_cancel',
          item: item ?? null,
          itemId: item ? getItemId(item) : null,
          sourceColumnId: from,
          overColumnId: null,
          toColumnId: null,
          beforeItemId: null,
          afterItemId: null,
          validationResult: null,
          dropAccepted: null,
          sourceItems: snapshot ?? itemsByColumn,
        });
        setDragItems(null);
      };
      const cancelDrag = () => setDragItems(null);
      if (!over || !item || !from) return emitCancelDrag();

      const to = resolveColumnId(over.id, over.data.current);
      if (!to) return emitCancelDrag();

      if (validateDrop && !validateDrop(from, to, item)) {
        emitTelemetry({
          phase: 'drop_rejected',
          item,
          itemId: getItemId(item),
          sourceColumnId: from,
          overColumnId: to,
          toColumnId: to,
          beforeItemId: null,
          afterItemId: null,
          validationResult: false,
          dropAccepted: false,
          sourceItems: snapshot ?? itemsByColumn,
        });
        return cancelDrag();
      }

      const activeId = getItemId(item);

      const dropPosition = deriveDropPosition({
        fromColumnId: from,
        toColumnId: to,
        overId: String(over.id),
        activeId,
        originalItems: itemsByColumn[to] ?? [],
        targetSnapshotItems: snapshot?.[to] ?? itemsByColumn[to] ?? [],
        getItemId,
        overSortableIndex: (over.data.current?.sortable as { index?: unknown } | undefined)?.index,
      });
      if (!dropPosition) {
        emitTelemetry({
          phase: 'drop_rejected',
          item,
          itemId: activeId,
          sourceColumnId: from,
          overColumnId: to,
          toColumnId: to,
          beforeItemId: null,
          afterItemId: null,
          validationResult: true,
          dropAccepted: false,
          sourceItems: snapshot ?? itemsByColumn,
        });
        return cancelDrag();
      }
      const { finalItems, beforeItemId, afterItemId } = dropPosition;

      // Eagerly publish the final order into dragItems so the next render —
      // which happens BEFORE TanStack Query's onMutate finishes its optimistic
      // cache update (it awaits cancelQueries, so the write lands a microtask
      // later) — already shows cards in their new positions. Without this,
      // sortable's strategy resets neighbor transforms with the items list
      // still in pre-drag order, producing a visible hop-and-slide. The
      // settle effect below clears dragItems once itemsByColumn converges.
      const finalSnapshot: Record<TColumnId, TItem[]> = { ...(snapshot ?? itemsByColumn) };
      finalSnapshot[to] = finalItems;
      if (from !== to) {
        finalSnapshot[from] = (snapshot?.[from] ?? itemsByColumn[from] ?? []).filter(
          (it) => getItemId(it) !== activeId
        );
      }

      const accepted = onDrop({
        item,
        fromColumnId: from,
        toColumnId: to,
        beforeItemId,
        afterItemId,
      });
      if (accepted === false) {
        emitTelemetry({
          phase: 'drop_rejected',
          item,
          itemId: activeId,
          sourceColumnId: from,
          overColumnId: to,
          toColumnId: to,
          beforeItemId,
          afterItemId,
          validationResult: true,
          dropAccepted: false,
          sourceItems: snapshot ?? itemsByColumn,
          snapshotItems: finalSnapshot,
        });
        return cancelDrag();
      }

      emitTelemetry({
        phase: 'drop_accepted',
        item,
        itemId: activeId,
        sourceColumnId: from,
        overColumnId: to,
        toColumnId: to,
        beforeItemId,
        afterItemId,
        validationResult: true,
        dropAccepted: true,
        sourceItems: snapshot ?? itemsByColumn,
        snapshotItems: finalSnapshot,
      });

      lastDropRef.current = { from, to, item, itemId: activeId, beforeItemId, afterItemId };
      setDragItems(finalSnapshot);
      clearSettleTimer();
      settleTimerRef.current = setTimeout(() => {
        const drop = lastDropRef.current;
        emitTelemetry({
          phase: 'settle_timeout',
          item: drop?.item ?? null,
          itemId: drop?.itemId ?? null,
          sourceColumnId: drop?.from ?? null,
          overColumnId: drop?.to ?? null,
          toColumnId: drop?.to ?? null,
          beforeItemId: drop?.beforeItemId ?? null,
          afterItemId: drop?.afterItemId ?? null,
          validationResult: null,
          dropAccepted: null,
          sourceItems: finalSnapshot,
        });
        settleTimerRef.current = null;
        lastDropRef.current = null;
        setDragItems(null);
      }, SETTLE_TIMEOUT_MS);
    },
    [
      activeItem,
      sourceColumnId,
      dragItems,
      itemsByColumn,
      getItemId,
      validateDrop,
      onDrop,
      onAutoScrollEnd,
      clearSettleTimer,
      emitTelemetry,
    ]
  );

  const handleDragCancel = useCallback(() => {
    emitTelemetry({
      phase: 'drag_cancel',
      item: activeItem,
      itemId: activeItem ? getItemId(activeItem) : null,
      sourceColumnId,
      overColumnId,
      toColumnId: overColumnId,
      beforeItemId: null,
      afterItemId: null,
      validationResult: null,
      dropAccepted: null,
      sourceItems: dragItems ?? itemsByColumn,
    });
    clearSettleTimer();
    lastDropRef.current = null;
    setActiveItem(null);
    setSourceColumnId(null);
    setOverColumnId(null);
    setDragItems(null);
    onAutoScrollEnd();
  }, [
    activeItem,
    sourceColumnId,
    overColumnId,
    dragItems,
    itemsByColumn,
    getItemId,
    onAutoScrollEnd,
    clearSettleTimer,
    emitTelemetry,
  ]);

  // Settle: once the consumer's data actually matches the dropped order in
  // the columns the drop touched (typically via the optimistic update in
  // onDrop's mutation), drop the snapshot so future external updates flow
  // through unmediated. Convergence is an ID-order comparison — NOT "any
  // render happened" — because the optimistic write lands a flush or two
  // after dragEnd and clearing early snaps the board back to the pre-drag
  // order mid drop-animation. If the consumer never converges, the
  // SETTLE_TIMEOUT_MS timer started at dragEnd clears the snapshot instead.
  useEffect(() => {
    if (activeItem !== null) return;
    if (dragItems === null) return;
    const drop = lastDropRef.current;
    if (!drop) return; // settling cleanup owned by the timer/cancel paths
    const converged =
      sameIdOrder(itemsByColumn[drop.to] ?? [], dragItems[drop.to] ?? [], getItemId) &&
      (drop.from === drop.to ||
        sameIdOrder(itemsByColumn[drop.from] ?? [], dragItems[drop.from] ?? [], getItemId));
    if (!converged) return;
    emitTelemetry({
      phase: 'settle_converged',
      item: drop.item,
      itemId: drop.itemId,
      sourceColumnId: drop.from,
      overColumnId: drop.to,
      toColumnId: drop.to,
      beforeItemId: drop.beforeItemId,
      afterItemId: drop.afterItemId,
      validationResult: null,
      dropAccepted: null,
      sourceItems: itemsByColumn,
      snapshotItems: dragItems,
    });
    clearSettleTimer();
    lastDropRef.current = null;
    setDragItems(null);
  }, [itemsByColumn, activeItem, dragItems, getItemId, clearSettleTimer, emitTelemetry]);

  const getColumnTargetState = (columnId: TColumnId): boolean | null => {
    if (!activeItem || !sourceColumnId) return null;
    if (columnId === sourceColumnId) return null;
    if (!validateDrop) return true;
    return validateDrop(sourceColumnId, columnId, activeItem);
  };

  const renderColumn = (col: BoardColumnConfig<TColumnId>, columnLayout: BoardColumnLayout) => {
    const source = dragItems ?? itemsByColumn;
    const items = source[col.id] ?? [];
    const itemIds = items.map(getItemId);
    return (
      <BoardColumn
        key={col.id}
        column={col}
        itemIds={itemIds}
        isOver={overColumnId === col.id}
        isValidTarget={getColumnTargetState(col.id)}
        orientation={columnLayout}
      >
        {items.map((item) => {
          const id = getItemId(item);
          const draggable = isItemDraggable ? isItemDraggable(item) : true;
          return (
            <BoardCard
              key={id}
              id={id}
              data={{ item, columnId: col.id }}
              disabled={!draggable}
              onClick={onCardClick ? () => onCardClick(item) : undefined}
            >
              {(state) => renderCard(item, state)}
            </BoardCard>
          );
        })}
      </BoardColumn>
    );
  };

  // Rail layout: pull one column to a left staging rail, stack the rest as
  // bands. Only in rows orientation; otherwise fall back to the flat layout.
  const railCol =
    orientation === 'rows' && railColumnId !== undefined
      ? columns.find((c) => c.id === railColumnId)
      : undefined;
  const bandCols = railCol ? columns.filter((c) => c.id !== railColumnId) : columns;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={orientation === 'rows' ? rowsCollisionDetection : closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragMove={onDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      {railCol ? (
        <div className="flex h-full w-full">
          {renderColumn(railCol, 'rail')}
          <div
            ref={scrollRef}
            className={`flex min-w-0 flex-1 flex-col gap-8 overflow-y-auto px-8 pb-24 pt-6 ${className ?? ''}`}
            style={{ scrollBehavior: 'auto' }}
          >
            {header}
            {bandCols.map((col) => renderColumn(col, 'rows'))}
            {footer}
          </div>
        </div>
      ) : (
        <div
          ref={scrollRef}
          className={`${orientation === 'rows' ? 'flex flex-col gap-8 overflow-y-auto' : 'flex overflow-x-auto'} ${height === undefined ? 'flex-1 min-h-0' : ''} ${className ?? ''}`}
          style={
            height === undefined ? { scrollBehavior: 'auto' } : { scrollBehavior: 'auto', height }
          }
        >
          {columns.map((col) => renderColumn(col, orientation))}
        </div>
      )}

      {/* Short drop animation: the overlay smoothly flies from the cursor to
          the dropped card's final DOM slot. Works because handleDragEnd has
          already written the final order into dragItems by this point, so the
          target slot is real. */}
      <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
        {activeItem ? (
          <BoardCard id={getItemId(activeItem)} isOverlay>
            {(state) => renderCard(activeItem, state)}
          </BoardCard>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
